/***************************************************************************/
"use client";
/***************************************************************************/

import { useContext } from "react";
import ConnectionContext from "./connection-context";
import { TFetchResponse, TOnAfterFetchArgs, TOnBeforeFetchArgs } from "./types";

/**
 * Helper function to safely retrieve JSON data from a response object.
 * @template TResponseData - The expected response data type.
 * @param {Response} response - The response object from fetch.
 * @returns {Promise<TResponseData | undefined>} Parsed JSON data or undefined if parsing fails.
 */
const retrieveResponseJSON = async <TResponseData>(response: Response) => {
  let data: TResponseData | undefined = undefined;
  try {
    data = (await response.json()) as TResponseData | undefined;
  } catch {
    data = undefined; // If parsing fails, return undefined data
  }
  return data;
};

/**
 * A hook that provides the core fetch functionality with queuing and cancellation support.
 * It can be used directly or within another hook to create specific HTTP methods (GET, POST, etc.).
 * @returns {Function} fetcher - A function to perform fetch requests.
 */
const useFetcher = () => {
  const connContext = useContext(ConnectionContext); // Fetch configuration from context

  /**
   * Core fetch function handling request execution, cancellation, and response processing.
   * @template TResponseData - The expected response data type.
   * @param {string | URL} url - The endpoint to send the request to.
   * @param {RequestInit} [options] - Fetch options such as method, headers, and body.
   * @param {{ timeout?: number; key?: string }} [config] - Configurations for timeout and queuing.
   * @returns {Promise<TFetchResponse<TResponseData | undefined>>} The fetch response object.
   */
  const fetcher = async <TResponseData>(
    url: string | URL,
    options?: RequestInit,
    config?: { timeout?: number; key?: string },
  ): Promise<TFetchResponse<TResponseData | undefined>> => {
    // Construct the URL with the root URL if needed
    const fetchUrl = /^http/i.test(url.toString())
      ? url
      : [connContext.rootUrl, url.toString().replace(/^\//, "")].join("/");

    // Merge default headers with headers from options
    const combinedOptions = {
      ...options,
      headers: {
        ...connContext.headers,
        ...(options?.headers ?? {}),
      },
    };

    // Prepare arguments for the onBefore hook and call it if provided
    const onBeforeArgs: TOnBeforeFetchArgs = {
      url: fetchUrl,
      options: combinedOptions,
      config,
    };

    if (connContext.onBefore) {
      connContext.onBefore(onBeforeArgs);
    }

    const controller = new AbortController();

    const timeoutId = setTimeout(
      () => controller.abort(),
      config?.timeout ?? 10000, // Default timeout is 10 seconds
    );

    // Execute fetch request with the controller signal for cancellation
    const response = await fetch(encodeURI(fetchUrl.toString()), {
      ...combinedOptions,
      signal: controller.signal,
    }).catch(() => new Response(JSON.stringify({}), { status: 500 }));

    clearTimeout(timeoutId); // Clear timeout once fetch completes

    // Parse the response data as JSON
    const data = await retrieveResponseJSON<TResponseData>(response);

    // Create structured fetch response
    const fetchResponse: TFetchResponse<TResponseData | undefined> = {
      data,
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
    };

    // Prepare arguments for the onAfter hook and call it if provided
    const onAfterArgs: TOnAfterFetchArgs = {
      url: response.url,
      response: fetchResponse,
      headers: response.headers,
    };

    if (connContext.onAfter) {
      connContext.onAfter(onAfterArgs);
    }

    return fetchResponse;
  };

  return fetcher;
};

export { useFetcher };
